import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { FiGrid, FiMessageSquare, FiCpu, FiUser, FiLogOut, FiSearch, FiBriefcase, FiAward, FiCheckCircle } from "react-icons/fi";
import Avatar from "./Avatar";
import { RatingText } from "./Stars";
import { apiRequest, timeAgoShort } from "../../api";
import { cardClass } from "./jobUtils";

const linkClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
    isActive ? "bg-brand-gradient text-white" : "text-gray-300 hover:bg-white/5 hover:text-white"
  }`;

// The signed-in member's left panel: photo, role + trust badges, rating, profile completion, work done,
// navigation (dashboard, messages, AI assistant, ...) and their latest chats.
export default function MemberSidebar({ user, onSignOut }) {
  const location = useLocation();
  const [chats, setChats] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const { ok, data } = await apiRequest("/core/cowork/chats/", { auth: true });
      if (alive && ok) setChats(data);
    };
    load();
    const timer = setInterval(load, 20000);
    window.addEventListener("cowork:chats", load);   // the Messages page announces every change
    return () => { alive = false; clearInterval(timer); window.removeEventListener("cowork:chats", load); };
  }, [location.pathname, location.search]);

  const unread = chats.reduce((n, c) => n + c.unread, 0);
  const isBuyer = user.role === "buyer";
  const percent = user.profile_completion ?? 0;
  const barColor = percent >= 80 ? "bg-emerald-500" : percent >= 50 ? "bg-amber-400" : "bg-red-400";

  return (
    <aside className="space-y-5 lg:sticky lg:top-24">
      {/* Identity */}
      <div className={`${cardClass} p-5 text-center`}>
        <Link to="/co-work/dashboard?tab=profile" className="inline-block" aria-label="Edit your profile photo">
          <Avatar src={user.avatar} name={user.full_name || user.email} size={88} className="mx-auto" />
        </Link>
        <h2 className="mt-3 text-lg font-bold leading-tight">{user.full_name || user.email}</h2>
        {(user.headline || user.company) && <p className="text-xs text-gray-400 mt-0.5">{user.headline || user.company}</p>}

        <div className="mt-3 flex flex-wrap justify-center gap-2">
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${isBuyer ? "bg-sky-500/15 text-sky-400" : "bg-violet-500/15 text-violet-400"}`}>
            {isBuyer ? "Buyer" : "Seller"}
          </span>
          {user.badge && (
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-300 inline-flex items-center gap-1">
              <FiAward size={11} /> {user.badge}
            </span>
          )}
        </div>

        <div className="mt-3 flex justify-center">
          <RatingText avg={user.rating_avg} count={user.rating_count} />
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2 text-center">
          <div className="rounded-lg bg-white/5 border border-navy-border py-2">
            <p className="text-xl font-extrabold text-accent-light">{user.jobs_done ?? 0}</p>
            <p className="text-[11px] text-gray-400">{isBuyer ? "Jobs completed" : "Jobs delivered"}</p>
          </div>
          <div className="rounded-lg bg-white/5 border border-navy-border py-2">
            <p className="text-xl font-extrabold text-accent-light">{user.rating_count ?? 0}</p>
            <p className="text-[11px] text-gray-400">Reviews</p>
          </div>
        </div>

        {/* Profile completion */}
        <div className="mt-4 text-left">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-gray-300 flex items-center gap-1">
              {percent >= 100 && <FiCheckCircle className="text-emerald-400" size={12} />} Profile completion
            </span>
            <span className="font-bold text-white">{percent}%</span>
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
            <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${percent}%` }} />
          </div>
          {percent < 100 && user.profile_missing?.length > 0 && (
            <p className="text-[11px] text-gray-400 mt-2">
              Add: {user.profile_missing.slice(0, 3).join(", ")}
              {user.profile_missing.length > 3 && ` +${user.profile_missing.length - 3} more`}.{" "}
              <Link to="/co-work/dashboard?tab=profile" className="text-accent-light hover:underline">Complete profile</Link>
            </p>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className={`${cardClass} p-2 space-y-0.5`}>
        <NavLink to="/co-work/dashboard" end className={() => linkClass({ isActive: location.pathname === "/co-work/dashboard" && !location.search.includes("tab=profile") })}>
          <FiGrid /> Dashboard
        </NavLink>
        <NavLink to="/co-work/messages" className={linkClass}>
          <FiMessageSquare /> <span className="flex-1">Messages</span>
          {unread > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-accent-light text-navy-darker text-xs font-bold flex items-center justify-center">{unread}</span>}
        </NavLink>
        <NavLink to="/co-work/assistant" className={linkClass}>
          <FiCpu /> AI Assistant
        </NavLink>
        <Link to={isBuyer ? "/co-work?find=1" : "/co-work#jobs"} className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-300 hover:bg-white/5 hover:text-white transition-colors">
          {isBuyer ? <FiSearch /> : <FiBriefcase />} {isBuyer ? "Find Seller" : "Browse Jobs"}
        </Link>
        <Link to="/co-work/dashboard?tab=profile" className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-300 hover:bg-white/5 hover:text-white transition-colors">
          <FiUser /> My Profile
        </Link>
        <button onClick={onSignOut} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
          <FiLogOut /> Sign out
        </button>
      </nav>

      {/* Chat history */}
      <div className={`${cardClass} p-4`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs uppercase tracking-widest text-white/50 font-semibold">Recent chats</h3>
          <Link to="/co-work/messages" className="text-xs text-accent-light hover:underline">See all</Link>
        </div>
        {chats.length === 0 ? (
          <p className="text-xs text-gray-500">
            No chats yet. {isBuyer ? "Message a seller from 'Find Seller'." : "Apply to a job, then message the buyer."}
          </p>
        ) : (
          <ul className="space-y-1">
            {chats.slice(0, 4).map((c) => (
              <li key={c.id}>
                <Link to={`/co-work/messages?c=${c.id}`} className="flex items-center gap-2.5 p-1.5 rounded-lg hover:bg-white/5">
                  <Avatar src={c.other.avatar} name={c.other.name} size={34} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className={`text-sm truncate ${c.unread ? "font-bold text-white" : "text-gray-200"}`}>{c.other.name}</span>
                      {c.last_message && <span className="text-[10px] text-gray-500 shrink-0">{timeAgoShort(c.last_message.created_at)}</span>}
                    </span>
                    <span className={`block text-xs truncate ${c.unread ? "text-gray-200" : "text-gray-500"}`}>
                      {c.last_message ? `${c.last_message.mine ? "You: " : ""}${c.last_message.body}` : "No messages yet"}
                    </span>
                  </span>
                  {c.unread > 0 && <span className="w-2 h-2 rounded-full bg-accent-light shrink-0" />}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}
